import type { ApiValidationIssue } from "./schemas";

const FIELD_LABELS: Readonly<Record<string, string>> = {
  accessNotes: "Access notes",
  amountMinor: "Price",
  applicationVersionNo: "Application version",
  bathroomCount: "Bathrooms",
  bedroomCount: "Bedrooms",
  bookingMode: "Pricing model",
  cleaningType: "Cleaning type",
  contentSha256: "Document integrity",
  countryCode: "Country code",
  currencyCode: "Currency",
  dayOfWeek: "Day",
  documentId: "Legal document",
  endTime: "End time",
  exceptions: "Availability exceptions",
  familyName: "Last name",
  fixedPriceAmountMinor: "Fixed price",
  startingPriceAmountMinor: "Starting price",
  givenName: "First name",
  "idempotency-key": "Request key",
  jobVersionNo: "Job version",
  label: "Label",
  linenSetCount: "Linen set count",
  line1: "Address line 1",
  line2: "Address line 2",
  lines: "Rate-card prices",
  limit: "Result limit",
  localeCode: "Locale",
  localDate: "Date",
  locality: "Town or city",
  mattressLengthCm: "Mattress length",
  mattressWidthCm: "Mattress width",
  minNoticeMinutes: "Minimum notice",
  mode: "Mode",
  offset: "Result offset",
  phoneE164: "Phone number",
  postalCode: "Postal code",
  postalCodes: "Postal codes",
  propertyId: "Property",
  propertyName: "Property name",
  propertyType: "Property type",
  providerVersionNo: "Provider version",
  quantity: "Quantity",
  rateCardVersionNo: "Rate-card version",
  reasonCode: "Reason",
  regionCode: "Region",
  requestedDate: "Requested date",
  requestedEndTime: "End time",
  requestedStartTime: "Start time",
  roomCount: "Rooms",
  roomLabel: "Room label",
  serviceId: "Service",
  services: "Services",
  size: "Bed size",
  sizeSqft: "Property size",
  specialInstructions: "Special instructions",
  startTime: "Start time",
  status: "Status",
  towelSetCount: "Towel set count",
  timezoneRegion: "Timezone",
  versionCode: "Document version",
  versionNo: "Version",
  windows: "Availability windows",
};

const SELECT_FIELDS = new Set([
  "bookingMode",
  "cleaningType",
  "countryCode",
  "currencyCode",
  "dayOfWeek",
  "localeCode",
  "mode",
  "propertyId",
  "propertyType",
  "reasonCode",
  "serviceId",
  "size",
  "status",
]);
const SAFE_REQUEST_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,99}$/u;

function terminalField(field: string | undefined): string | undefined {
  if (field === undefined) return undefined;
  return [...field.split(".")].reverse().find((segment) => !/^\d+$/u.test(segment));
}

function humanize(value: string): string {
  const spaced = value
    .replaceAll(/([a-z0-9])([A-Z])/gu, "$1 $2")
    .replaceAll(/[_-]+/gu, " ")
    .trim();
  return spaced.length === 0 ? "Field" : `${spaced[0]?.toUpperCase() ?? ""}${spaced.slice(1)}`;
}

export function validationFieldLabel(field: string | undefined): string | null {
  const terminal = terminalField(field);
  if (terminal === undefined) return null;
  return FIELD_LABELS[terminal] ?? humanize(terminal);
}

function numberText(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toLocaleString("en-US");
}

function minorCurrencyMessage(
  field: string | undefined,
  value: number | undefined,
  comparison: "minimum" | "maximum",
): string | undefined {
  if (
    value === undefined ||
    !["amountMinor", "fixedPriceAmountMinor", "startingPriceAmountMinor"].includes(
      terminalField(field) ?? "",
    )
  ) {
    return undefined;
  }
  return `Enter ${comparison === "minimum" ? "at least" : "no more than"} $${(value / 100).toFixed(2)}.`;
}

function invalidFormatMessage(field: string | undefined): string {
  switch (terminalField(field)) {
    case "phoneE164":
      return "Enter a 10-digit US phone number.";
    case "timezoneRegion":
      return "Enter an IANA timezone, for example America/Los_Angeles.";
    case "requestedDate":
    case "localDate":
      return "Enter a real date in YYYY-MM-DD format.";
    case "requestedStartTime":
    case "requestedEndTime":
    case "startTime":
    case "endTime":
      return "Enter a time in 24-hour HH:MM format.";
    case "postalCode":
    case "postalCodes":
      return "Enter a valid postal code.";
    case "countryCode":
      return "Enter a two-letter country code.";
    default:
      return "Use the required format.";
  }
}

function duplicateMessage(field: string | undefined): string {
  switch (terminalField(field)) {
    case "serviceId":
      return "Choose each service only once.";
    case "localDate":
      return "Use each date only once.";
    case "postalCode":
    case "postalCodes":
      return "Use each postal code only once.";
    case "label":
      return "Use a unique label.";
    default:
      return "Remove the duplicate value.";
  }
}

export function validationIssueMessage(issue: ApiValidationIssue): string {
  const { constraints, field, reason } = issue;
  switch (reason) {
    case "required":
      return "Enter a value.";
    case "unknown_field":
      return "Remove this unsupported field and try again.";
    case "invalid_type":
      return "Enter the expected type of value.";
    case "invalid_format":
      return invalidFormatMessage(field);
    case "invalid_shape":
      return "Review this section and try again.";
    case "invalid_value":
      return SELECT_FIELDS.has(terminalField(field) ?? "")
        ? "Choose a valid option."
        : "Enter a valid value.";
    case "too_short":
      return constraints?.minimum === undefined
        ? "Enter more information."
        : `Use at least ${numberText(constraints.minimum)} ${constraints.unit === "bytes" ? "bytes" : "characters"}.`;
    case "too_long":
      return constraints?.maximum === undefined
        ? "Use a shorter value."
        : `Use no more than ${numberText(constraints.maximum)} ${constraints.unit === "bytes" ? "bytes" : "characters"}.`;
    case "too_small":
      return (
        minorCurrencyMessage(field, constraints?.minimum, "minimum") ??
        (constraints?.minimum === undefined
          ? "Enter a larger value."
          : `Enter ${numberText(constraints.minimum)} or more.`)
      );
    case "too_large":
      return (
        minorCurrencyMessage(field, constraints?.maximum, "maximum") ??
        (constraints?.maximum === undefined
          ? "Enter a smaller value."
          : `Enter ${numberText(constraints.maximum)} or less.`)
      );
    case "too_few_items":
      return constraints?.minimum === undefined
        ? "Add at least one item."
        : `Add at least ${numberText(constraints.minimum)} item${constraints.minimum === 1 ? "" : "s"}.`;
    case "too_many_items":
      return constraints?.maximum === undefined
        ? "Remove one or more items."
        : `Use no more than ${numberText(constraints.maximum)} items.`;
    case "invalid_step":
      return constraints?.multipleOf === undefined
        ? "Enter a permitted increment."
        : `Use increments of ${numberText(constraints.multipleOf)}.`;
    case "duplicate":
      return duplicateMessage(field);
    case "invalid_order":
      return "The start time must be before the end time.";
    case "overlap":
      return "This entry overlaps another one.";
    case "inconsistent":
      return "This value conflicts with another selection.";
    case "unsupported":
      return "This option is not currently supported.";
    case "body_not_allowed":
      return "This action does not accept additional information.";
    case "malformed_json":
      return "The submitted request could not be read. Refresh the page and try again.";
    default:
      return "Check this value and try again.";
  }
}

export function validationIssueSummary(issue: ApiValidationIssue): string {
  const message = validationIssueMessage(issue);
  const label = validationFieldLabel(issue.field);
  if (label === null) return message;
  return `${label}: ${message[0]?.toLowerCase() ?? ""}${message.slice(1)}`;
}

export function withRequestReference(message: string, requestId: string | null): string {
  if (requestId === null || !SAFE_REQUEST_ID.test(requestId)) return message;
  const separator = /[.!?]$/u.test(message) ? "" : ".";
  return `${message}${separator} Request ID: ${requestId}`;
}
